/**
 * Proof-of-possession & key-transparency primitives (H1) — the TS half of the
 * Rust `wiser_e2e::pop` module. Byte-identical: RFC 8032 Ed25519 is deterministic,
 * so the same (seed, message) yields the same 64-byte signature here (tweetnacl
 * `nacl.sign`) and in Rust (ed25519-dalek). The payload layouts + a known-answer
 * signature are pinned in ../vectors/wire-vectors.json (`pop_v1`) and asserted by
 * both conformance tests; neither side may change them without both going red.
 *
 * Construction (see the Rust module and wiserkeys/docs/proof-of-possession.md):
 *  - A per-user Ed25519 **identity key** is the long-lived signing root, enrolled
 *    once and self-signed (proof of possession of the identity private key).
 *  - Each X25519 **encryption key** is signed by the identity key; the signed
 *    payload binds BOTH the predecessor's public-key bytes (`prev_enc_pk`) AND the
 *    directory `key_version`, so the keys form a fork-detectable, version-pinned
 *    lineage anchored at the identity key. Binding `key_version` is the
 *    anti-rollback measure: a malicious directory can't serve an older signed key
 *    as a higher "current" version or splice a rotation out, and `verifyChain`
 *    enforces strictly-increasing, contiguous-from-1 versions. The client still
 *    signs offline (enroll=1, rotation=prev+1, both known to it) without a
 *    wiserkeys round-trip. The encryption keys are X25519 and cannot themselves
 *    sign (no XEdDSA), so the identity key — not the retiring enc key — anchors
 *    the chain.
 *
 *  WIRE FORMAT (BREAKING): the signed enc-key payload changed in v2 — it now
 *  includes the 4-byte big-endian `key_version` and the domain separator is
 *  `wiser-e2e/pop/enc-key/v2`. v1 signatures do not verify under v2; any
 *  identity_sig stored in wiserkeys under v1 must be re-attested. Kept
 *  byte-identical with the Rust `pop.rs` v2 layout.
 */

import nacl from 'tweetnacl';

/** Ed25519 identity public key length. */
export const IDENTITY_PK_LEN = 32;
/** Ed25519 identity seed (private key) length. */
export const IDENTITY_SEED_LEN = 32;
/** Ed25519 detached signature length. */
export const POP_SIG_LEN = 64;
/** X25519 encryption public key length (the keys stored in wiserkeys). */
export const ENC_PK_LEN = 32;
/** A user id is a 16-byte UUID (network order). */
export const USER_ID_LEN = 16;

/** Domain separator for the identity-key self-signature. */
export const DOMAIN_IDENTITY = new TextEncoder().encode('wiser-e2e/pop/identity-reg/v1');
/** Domain separator for an identity-signed encryption-key attestation. Bumped to
 *  `v2` when `key_version` joined the signed payload (see file header). */
export const DOMAIN_ENC_KEY = new TextEncoder().encode('wiser-e2e/pop/enc-key/v2');

/** Sentinel `prev_enc_pk` for a v1 (enroll) attestation: no predecessor. */
export const NO_PREV_ENC_PK = new Uint8Array(ENC_PK_LEN); // 32 zero bytes

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const len = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/** Parse a UUID string ("xxxxxxxx-xxxx-…") into its 16 network-order bytes. */
export function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) throw new Error(`invalid uuid: ${uuid}`);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) out[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * Canonical bytes the identity key self-signs on registration:
 * DOMAIN_IDENTITY || user_id(16) || identity_pk(32).
 */
export function identityRegistrationPayload(userId: Uint8Array, identityPk: Uint8Array): Uint8Array {
  if (userId.length !== USER_ID_LEN) throw new Error(`user_id must be ${USER_ID_LEN} bytes`);
  if (identityPk.length !== IDENTITY_PK_LEN) throw new Error(`identity_pk must be ${IDENTITY_PK_LEN} bytes`);
  return concatBytes([DOMAIN_IDENTITY, userId, identityPk]);
}

/** Encode a u32 as 4 big-endian bytes (matches Rust `u32::to_be_bytes`). */
function u32be(n: number): Uint8Array {
  if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) {
    throw new Error(`key_version must be a u32, got ${n}`);
  }
  const out = new Uint8Array(4);
  out[0] = (n >>> 24) & 0xff;
  out[1] = (n >>> 16) & 0xff;
  out[2] = (n >>> 8) & 0xff;
  out[3] = n & 0xff;
  return out;
}

/**
 * Canonical bytes the identity key signs to attest an encryption key:
 * DOMAIN_ENC_KEY || user_id(16) || identity_pk(32) || key_version(4, BE) || enc_pk(32) || prev_enc_pk(32).
 * For a v1 enroll pass keyVersion=1 and prevEncPk=NO_PREV_ENC_PK; for a rotation
 * pass the new version and the predecessor's public key. Binding key_version is
 * what makes a directory rollback detectable (see file header).
 */
export function encKeyPayload(
  userId: Uint8Array,
  identityPk: Uint8Array,
  keyVersion: number,
  encPk: Uint8Array,
  prevEncPk: Uint8Array,
): Uint8Array {
  if (userId.length !== USER_ID_LEN) throw new Error(`user_id must be ${USER_ID_LEN} bytes`);
  if (identityPk.length !== IDENTITY_PK_LEN) throw new Error(`identity_pk must be ${IDENTITY_PK_LEN} bytes`);
  if (encPk.length !== ENC_PK_LEN) throw new Error(`enc_pk must be ${ENC_PK_LEN} bytes`);
  if (prevEncPk.length !== ENC_PK_LEN) throw new Error(`prev_enc_pk must be ${ENC_PK_LEN} bytes`);
  return concatBytes([DOMAIN_ENC_KEY, userId, identityPk, u32be(keyVersion), encPk, prevEncPk]);
}

/** The Ed25519 public key for a 32-byte identity seed. */
export function identityPublicFromSeed(seed: Uint8Array): Uint8Array {
  if (seed.length !== IDENTITY_SEED_LEN) throw new Error(`seed must be ${IDENTITY_SEED_LEN} bytes`);
  return nacl.sign.keyPair.fromSeed(seed).publicKey;
}

/** Raw Ed25519 detached signature of `msg` under a 32-byte seed. */
export function sign(seed: Uint8Array, msg: Uint8Array): Uint8Array {
  if (seed.length !== IDENTITY_SEED_LEN) throw new Error(`seed must be ${IDENTITY_SEED_LEN} bytes`);
  const kp = nacl.sign.keyPair.fromSeed(seed);
  return nacl.sign.detached(msg, kp.secretKey);
}

/** Verify a raw Ed25519 detached signature. */
export function verify(publicKey: Uint8Array, msg: Uint8Array, sig: Uint8Array): boolean {
  if (publicKey.length !== IDENTITY_PK_LEN || sig.length !== POP_SIG_LEN) return false;
  return nacl.sign.detached.verify(msg, sig, publicKey);
}

/** Sign the identity-key registration (self-signature). */
export function signIdentityRegistration(seed: Uint8Array, userId: Uint8Array): Uint8Array {
  const identityPk = identityPublicFromSeed(seed);
  return sign(seed, identityRegistrationPayload(userId, identityPk));
}

/** Verify an identity key's self-signature. */
export function verifyIdentityRegistration(identityPk: Uint8Array, userId: Uint8Array, selfSig: Uint8Array): boolean {
  return verify(identityPk, identityRegistrationPayload(userId, identityPk), selfSig);
}

/** Sign an encryption-key attestation with the identity seed (→ identity_sig).
 *  `keyVersion` is this key's directory version (1 for enroll, prev+1 for a
 *  rotation). */
export function signEncKey(
  identitySeed: Uint8Array,
  userId: Uint8Array,
  keyVersion: number,
  encPk: Uint8Array,
  prevEncPk: Uint8Array,
): Uint8Array {
  const identityPk = identityPublicFromSeed(identitySeed);
  return sign(identitySeed, encKeyPayload(userId, identityPk, keyVersion, encPk, prevEncPk));
}

/** Verify an encryption-key attestation against the user's identity public key. */
export function verifyEncKey(
  identityPk: Uint8Array,
  userId: Uint8Array,
  keyVersion: number,
  encPk: Uint8Array,
  prevEncPk: Uint8Array,
  identitySig: Uint8Array,
): boolean {
  return verify(identityPk, encKeyPayload(userId, identityPk, keyVersion, encPk, prevEncPk), identitySig);
}

/**
 * One link in a user's key history, as read from wiserkeys. `keyVersion` is the
 * directory's version; as of the v2 payload it is part of the signed bytes AND
 * `verifyChain` enforces strictly-increasing, contiguous-from-1 versions — the
 * anti-rollback guarantee. The lineage is also bound by `prevEncPk`.
 */
export interface ChainLink {
  keyVersion: number;
  encPk: Uint8Array;
  prevEncPk: Uint8Array;
  identitySig: Uint8Array;
}

function eqBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Verify a full key history: identity self-signature, then for every link a
 * strictly-increasing, contiguous-from-1 `keyVersion` (link N must be version N),
 * the structural continuity of the lineage (`prevEncPk` equals the predecessor's
 * `encPk`; the first link declares no predecessor), and the signature over the
 * version-pinned payload. `links` must be in directory order (ascending
 * keyVersion). The contiguity check is the anti-rollback guarantee: a re-served
 * older prefix downgrades the current key, a spliced-out rotation leaves a gap,
 * and a relabelled key fails its (now version-bound) signature. Returns `{ ok }`;
 * on failure `reason` is a human-surfaceable "key changed / unverified" message.
 * Consumers MUST refuse to seal to an unverified current key.
 */
export function verifyChain(
  identityPk: Uint8Array,
  userId: Uint8Array,
  selfSig: Uint8Array,
  links: ChainLink[],
): { ok: boolean; reason?: string } {
  if (!verifyIdentityRegistration(identityPk, userId, selfSig)) {
    return { ok: false, reason: 'identity self-signature invalid' };
  }
  if (links.length === 0) return { ok: false, reason: 'empty key chain' };
  let prev: ChainLink | null = null;
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    // Versions must be exactly 1, 2, 3, … — rejects rollback, splice, reorder.
    const expectedVersion = i + 1;
    if (link.keyVersion !== expectedVersion) {
      return {
        ok: false,
        reason: `non-contiguous key history: expected version ${expectedVersion}, got ${link.keyVersion}`,
      };
    }
    const expectedPrev = prev === null ? NO_PREV_ENC_PK : prev.encPk;
    if (!eqBytes(link.prevEncPk, expectedPrev)) {
      return { ok: false, reason: `broken lineage at version ${link.keyVersion}: prevEncPk does not match predecessor` };
    }
    if (!verifyEncKey(identityPk, userId, link.keyVersion, link.encPk, link.prevEncPk, link.identitySig)) {
      return { ok: false, reason: `signature invalid at version ${link.keyVersion}` };
    }
    prev = link;
  }
  return { ok: true };
}
